import React, { useContext, useEffect, useMemo, useState } from 'react';
import { RowNode } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { TextField, IconButton } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import AgentQAItemsModel from 'models/AgentQAItemsModel';
import { AgentQAModel } from 'models/AgentQAModel';
import { IAgentQAEdit, IAgentQAGroupedItem } from 'types/agentQA';
import { useForceRender } from 'hooks/useForceRender';
import Grid from 'CMFW/grid/Grid';
import DeleteButton from 'CMFW/button/DeleteButton';
import ModuleContext from 'CMFW/context/ModuleContext';
import ValidatorHeader from 'components/ValidatorHeader';
import PrioritySelector from 'components/PrioritySelector';
import AgentQASummary from './AgentQASummary';
import AgentQABulkActions from './AgentQABulkActions';
import { IFields } from 'CMFW/grid/fields';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import { agentQAItemInfoRenderer } from 'CMFW/grid/cellRenderers/agentQAItemInfo';

let currentFilter: { type: string; value: string } | null = null;
let qaTextFilter = '';

const AgentQADetails: React.FC = () => {
  const model = useContext(ModuleContext) as AgentQAModel;
  const jobId = model.itemId;

  const forceRender = useForceRender();
  const [activeFilter, setActiveFilter] = useState<{ type: string; value: string } | null>(null);
  const [jobName, setJobName] = useState<string>('');
  const [agentType, setAgentType] = useState<string>('');
  const [jobPriority, setJobPriority] = useState<number | undefined>(undefined);
  const [jobStatus, setJobStatus] = useState<number>(0);
  const [numberOfRuns, setNumberOfRuns] = useState<number>(1);
  const [filterInput, setFilterInput] = useState('');
  const [filterText, setFilterText] = useState('');
  currentFilter = activeFilter;

  const itemsModel = useMemo(() => new AgentQAItemsModel({ render: forceRender }), [forceRender]);

  useEffect(() => {
    if (jobId > 0) {
      itemsModel.setJobId(jobId);
      itemsModel.setAgentQAModel(model);
      setActiveFilter(null);

      // Load job details to get name, agent type, priority, and status
      model.loadItemData(jobId).then((data: IAgentQAEdit) => {
        if (data) {
          if (data.name) {
            setJobName(data.name);
          }
          if (data.agent_type) {
            setAgentType(data.agent_type);
          }
          if (data.priority !== undefined) {
            setJobPriority(data.priority);
          }
          if (data.status !== undefined) {
            setJobStatus(data.status);
          }
          if (data.number_of_runs !== undefined) {
            setNumberOfRuns(data.number_of_runs);
          }
        }
      });
    }
  }, [jobId, itemsModel, model]);

  useEffect(() => {
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [activeFilter, itemsModel]);

  // Debounce text filter input
  useEffect(() => {
    const timer = setTimeout(() => setFilterText(filterInput), 300);
    return () => clearTimeout(timer);
  }, [filterInput]);

  // Update module-level variable and trigger grid re-filtering
  useEffect(() => {
    qaTextFilter = filterText;
    itemsModel.loadingGrid = true;
    itemsModel.render();
    setTimeout(() => {
      itemsModel.loadingGrid = false;
      itemsModel.render();
    }, 0);
  }, [filterText, itemsModel]);

  const isFilterActive = () => currentFilter !== null || qaTextFilter.trim() !== '';

  const isRowVisible = (node: RowNode) => {
    const data = node.data as IAgentQAGroupedItem | undefined;
    if (!data) return true;

    // Handle categorical filter (pass/fail/status)
    if (currentFilter) {
      // Handle decision filter (pass/fail)
      if (currentFilter.type === 'decision' || currentFilter.type === 'test_result') {
        if (currentFilter.value === 'pass' && data.pass_count === 0) return false;
        if (currentFilter.value === 'fail' && data.fail_count === 0) return false;
      }

      // Handle status filter (processing/error)
      if (currentFilter.type === 'status') {
        if (currentFilter.value === 'processing' && data.pending_count === 0) return false;
        if (currentFilter.value === 'error' && data.error_count === 0) return false;
      }
    }

    // Handle text filter
    if (qaTextFilter.trim() !== '') {
      const t = qaTextFilter.trim();
      let regex: RegExp | null = null;
      try {
        regex = new RegExp(t, 'i');
      } catch {
        regex = null;
      }

      const values = [
        String(data.runs?.[0]?.id ?? ''),
        String(data.item_info?.us_translation ?? ''),
        String(data.item_info?.target_translation ?? '')
      ];

      if (regex) {
        if (!values.some((value) => regex!.test(value))) return false;
      } else {
        const lowered = t.toLowerCase();
        if (!values.some((value) => value.toLowerCase().includes(lowered))) return false;
      }
    }

    return true;
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.async_task_status]: asyncTaskStatusRenderer,
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
      [IFields.item_info]: agentQAItemInfoRenderer,
      [IFields.result_column]: 'agentQAResultColumnRenderer',
      [IFields.retry_action]: 'agentQARetryRenderer',
    }),
    [],
  );

  const handleChangePriority = async (newPriority: number) => {
    await model.changePriority(jobId, newPriority);
    setJobPriority(newPriority);
  };

  const handleActionComplete = () => {
    // Refresh items after bulk action completes
    itemsModel.reload();
    forceRender();
  };

  // Guard render after establishing hooks
  if (!jobId || jobId === -1) {
    return null;
  }

  return (
    <Container>
      <ValidatorHeader
        id={jobId}
        name={jobName}
        idLabel="Job"
        bulkActions={
          <>
            <AgentQABulkActions
              itemsModel={itemsModel}
              agentQAModel={model}
              jobStatus={jobStatus}
              jobName={jobName}
              onActionComplete={handleActionComplete}
            />
            <PrioritySelector
              currentPriority={jobPriority ?? 1}
              onChangePriority={handleChangePriority}
              isProcessing={
                itemsModel.gridData?.some((group: IAgentQAGroupedItem) => group.pending_count > 0) ?? false
              }
            />
          </>
        }
        customTitle={
          <>
            {jobName || `Job #${jobId}`}
            {agentType && <AgentTypeBadge>Agent: {agentType}</AgentTypeBadge>}
            {numberOfRuns > 1 && <RunsBadge>{numberOfRuns} runs per item</RunsBadge>}
          </>
        }
        enableMockCall={false}
        itemsModel={itemsModel}
      />

      <AgentQASummary
        itemsModel={itemsModel}
        onFilterChange={setActiveFilter}
        activeFilter={activeFilter}
        onClearFilter={() => setActiveFilter(null)}
      />

      <FilterRow>
        <TextFieldStyled
          variant="outlined"
          size="small"
          placeholder="Filter by text / id / translation"
          value={filterInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterInput(e.target.value)}
        />
        <IconButton
          size="small"
          onClick={() => {
            setFilterInput('');
            setFilterText('');
          }}
          disabled={!filterInput}
        >
          <FontAwesomeIcon icon={faTimes} />
        </IconButton>
      </FilterRow>

      <GridContainer>
        <ModuleContext.Provider value={itemsModel}>
          <Grid cellRenderers={cellRenderers} isFilterActive={isFilterActive} isRowVisible={isRowVisible} />
        </ModuleContext.Provider>
      </GridContainer>

      <DeleteButtonContainer>
        <ModuleContext.Provider value={model}>
          <DeleteButton />
        </ModuleContext.Provider>
      </DeleteButtonContainer>
    </Container>
  );
};

export default AgentQADetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const AgentTypeBadge = styled.span`
  margin-left: 12px;
  font-size: 13px;
  color: #666;
  font-style: italic;
  font-weight: normal;
`;

const RunsBadge = styled.span`
  margin-left: 12px;
  font-size: 12px;
  color: #1565c0;
  background: #e3f2fd;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
`;

const TextFieldStyled = styled(TextField)`
  flex-grow: 1;
`;

const DeleteButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-top: 4px;
`;
